import {
	DndContext,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { type ReactNode, useCallback, useState } from "react";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useAdminFormEditorPaths } from "../../hooks/use-admin-form-editor-paths";
import { formBuilderCollisionDetection } from "../../lib/form-builder-dnd-collision";
import {
	FORM_BUILDER_DROP_ROOT,
	resolveFormBuilderDropTarget,
	resolveSectionAppendFromDropOverId,
} from "../../lib/form-builder-drop-target";
import { useFormEditorStore } from "../../state/form-editor-store";
import type { PaletteFieldType } from "../../types";
import { FormBuilderCanvas } from "./form-builder-canvas";
import { FormBuilderDragOverlayContent } from "./form-builder-drag-overlay";
import { PALETTE_PREFIX } from "./form-builder-palette";
import { translatePaletteFieldTypeLabel as translatePaletteFieldTypeLabelFn } from "./form-builder-palette-label";
import { FormBuilderSidebar } from "./form-builder-sidebar";

function FormBuilderDragOverlayActive({
	activeDragId,
}: {
	activeDragId: string;
}) {
	const nodes = useFormEditorStore((s) => s.nodes);
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();
	return (
		<FormBuilderDragOverlayContent
			activeDragId={activeDragId}
			nodes={nodes}
			t={t}
			paths={paths}
		/>
	);
}

/**
 * Form Builder tab: canvas, field palette, properties rail, and @dnd-kit drag-and-drop.
 */
export function FormBuilderPanel({ header }: { header?: ReactNode }) {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();

	const translatePaletteTypeLabel = useCallback(
		(fieldType: PaletteFieldType) =>
			translatePaletteFieldTypeLabelFn(t, paths, fieldType),
		[t, paths],
	);

	const [activeDragId, setActiveDragId] = useState<string | null>(null);

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
		useSensor(KeyboardSensor),
	);

	const onDragStart = useCallback((event: DragStartEvent) => {
		setActiveDragId(String(event.active.id));
	}, []);

	const onDragEnd = useCallback(
		(event: DragEndEvent) => {
			setActiveDragId(null);
			const { active, over } = event;
			if (!over) return;

			const {
				nodes,
				rootIds,
				addFieldFromPalette,
				reorderRoot,
				reorderInSection,
				moveFieldToParent,
			} = useFormEditorStore.getState();

			const activeId = String(active.id);
			const overId = String(over.id);
			const ctx = { nodes, rootIds };

			if (activeId.startsWith(PALETTE_PREFIX)) {
				const fieldType = activeId.slice(
					PALETTE_PREFIX.length,
				) as PaletteFieldType;
				const target = resolveFormBuilderDropTarget(overId, ctx);
				if (!target) return;
				const label = translatePaletteTypeLabel(fieldType);
				if (target.kind === "root") {
					addFieldFromPalette(
						fieldType,
						{
							parent: "root",
							index: target.index,
						},
						label,
					);
				} else {
					addFieldFromPalette(
						fieldType,
						{
							parent: target.sectionId,
							index: target.index,
						},
						label,
					);
				}
				return;
			}

			const activeNode = nodes[activeId];
			if (!activeNode || activeNode.kind !== "field") {
				if (rootIds.includes(activeId) && rootIds.includes(overId)) {
					reorderRoot(
						arrayMove(
							rootIds,
							rootIds.indexOf(activeId),
							rootIds.indexOf(overId),
						),
					);
				}
				return;
			}

			{
				const appendSectionId = resolveSectionAppendFromDropOverId(
					overId,
					nodes,
				);
				if (appendSectionId !== null) {
					const parent = nodes[appendSectionId];
					if (parent?.kind === "section") {
						moveFieldToParent(
							activeId,
							appendSectionId,
							parent.childFieldIds.length,
						);
					}
					return;
				}
			}

			if (overId === FORM_BUILDER_DROP_ROOT) {
				moveFieldToParent(activeId, "root", rootIds.length);
				return;
			}

			if (activeNode.parentId === "root" && rootIds.includes(overId)) {
				const overNode = nodes[overId];
				if (overNode?.kind === "field") {
					reorderRoot(
						arrayMove(
							rootIds,
							rootIds.indexOf(activeId),
							rootIds.indexOf(overId),
						),
					);
					return;
				}
			}

			if (activeNode.parentId !== "root") {
				const sectionId = activeNode.parentId;
				const parent = nodes[sectionId];
				if (parent?.kind === "section") {
					const childIds = parent.childFieldIds;
					if (childIds.includes(activeId) && childIds.includes(overId)) {
						reorderInSection(
							sectionId,
							arrayMove(
								childIds,
								childIds.indexOf(activeId),
								childIds.indexOf(overId),
							),
						);
						return;
					}
				}
			}

			const target = resolveFormBuilderDropTarget(overId, ctx);
			if (!target) return;
			if (target.kind === "root") {
				moveFieldToParent(activeId, "root", target.index);
			} else {
				moveFieldToParent(activeId, target.sectionId, target.index);
			}
		},
		[translatePaletteTypeLabel, t, paths],
	);

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={formBuilderCollisionDetection}
			onDragStart={onDragStart}
			onDragEnd={onDragEnd}
		>
			<div className="flex min-h-form-builder-shell flex-1 bg-background-primary">
				<div className="flex min-w-0 flex-1 flex-col">
					{header ? <div className="shrink-0 px-6 pt-4">{header}</div> : null}
					<FormBuilderCanvas />
				</div>
				<FormBuilderSidebar />
			</div>

			<DragOverlay dropAnimation={null}>
				{activeDragId ? (
					<FormBuilderDragOverlayActive activeDragId={activeDragId} />
				) : null}
			</DragOverlay>
		</DndContext>
	);
}
